import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components';
import {
  PurchaseOrderDetail,
  PurchaseOrderStatus,
  PurchaseReceiveItem,
  ReceiveItemInput,
  ReceiveItemRowError,
} from '@/types/purchase-order';
import {
  approvePurchaseOrder,
  cooApprovePurchaseOrder,
  closePurchaseOrder,
  getPurchaseOrderById,
  getPurchaseOrderReceiveItems,
  receivePurchaseItems,
  ReceivePurchaseItemsError,
  rejectPurchaseOrder,
} from '@/services/api/purchaseOrders';
import { getListOem } from '@/services/api/oems';
import { getPurchaseOrderApprovers } from '@/services/api/poApprovers';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserData, getUserEmail } from '@/utils';
import {
  PurchaseOrderDetailContent,
  PurchaseOrderDetailHeader,
  PurchaseOrderStatusTimeline,
  ReceiveItemsModal,
  RejectPurchaseOrderModal,
} from './components';

const currentUser = () => getUserData()?.name || getUserEmail();

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [purchaseOrder, setPurchaseOrder] =
    useState<PurchaseOrderDetail | null>(null);
  const [oemName, setOemName] = useState<string>('');
  const [modelName, setModelName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const [isApproving, setIsApproving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [isReceiving, setIsReceiving] = useState(false);

  // Approver membership for the logged-in user, from the backend allowlist.
  // Gates the action buttons (UX only — the backend enforces the real check).
  const [isLayer1Approver, setIsLayer1Approver] = useState(false);
  const [isCooApprover, setIsCooApprover] = useState(false);

  // Received items — fetched from the paginated receive-items endpoint
  const [receiveItems, setReceiveItems] = useState<PurchaseReceiveItem[]>([]);
  const [receivedTotal, setReceivedTotal] = useState(0);
  const [receivePage, setReceivePage] = useState(1);
  const [receivePageSize, setReceivePageSize] = useState(10);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [receiveErrors, setReceiveErrors] = useState<ReceiveItemRowError[]>();

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const detail = await getPurchaseOrderById(id);
      setPurchaseOrder(detail);

      // The model name now comes with the detail response, so only the OEM name
      // still needs resolving from a list.
      const oemRes = await getListOem({ page: '1', size: '1000' }).catch(
        () => null
      );
      setOemName(
        oemRes?.data.find((oem) => oem.ID === detail.oemID)?.name || ''
      );
      setModelName(detail.modelName || '');
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load purchase order detail')
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Resolve whether the current user is an approver (per layer) from the API.
  // Fails closed: any error leaves both flags false, so no buttons show.
  useEffect(() => {
    let cancelled = false;
    const loadApproverMembership = async () => {
      try {
        const email = (getUserEmail() || '').trim().toLowerCase();
        if (!email) return;
        const approvers = await getPurchaseOrderApprovers();
        if (cancelled) return;
        const active = approvers.filter(
          (a) => a.isActive && a.email.trim().toLowerCase() === email
        );
        setIsLayer1Approver(active.some((a) => a.layer === 'LAYER_1'));
        setIsCooApprover(active.some((a) => a.layer === 'COO'));
      } catch {
        if (!cancelled) {
          setIsLayer1Approver(false);
          setIsCooApprover(false);
        }
      }
    };
    loadApproverMembership();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchReceiveItems = useCallback(async () => {
    if (!id) return;
    setReceiveLoading(true);
    try {
      const res = await getPurchaseOrderReceiveItems(id, {
        page: receivePage,
        size: receivePageSize,
      });
      setReceiveItems(res.data);
      setReceivedTotal(res.pagination.total);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load received units')
      );
    } finally {
      setReceiveLoading(false);
    }
  }, [id, receivePage, receivePageSize]);

  useEffect(() => {
    fetchReceiveItems();
  }, [fetchReceiveItems]);

  const handleApprove = async () => {
    if (!purchaseOrder || !id) return;
    setIsApproving(true);
    try {
      await approvePurchaseOrder(id, {
        updatedBy: currentUser(),
        updatedByEmail: getUserEmail(),
      });
      showSuccessToast('Success', 'Purchase order approved');
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to approve purchase order')
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleCooApprove = async () => {
    if (!purchaseOrder || !id) return;
    setIsApproving(true);
    try {
      await cooApprovePurchaseOrder(id, {
        updatedBy: currentUser(),
        updatedByEmail: getUserEmail(),
      });
      showSuccessToast('Success', 'Purchase order approved by COO');
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to approve purchase order')
      );
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (rejectionReason: string) => {
    if (!purchaseOrder || !id) return;
    setIsRejecting(true);
    try {
      await rejectPurchaseOrder(id, {
        rejectionReason,
        updatedBy: currentUser(),
        updatedByEmail: getUserEmail(),
      });
      showSuccessToast('Success', 'Purchase order rejected');
      setIsRejectOpen(false);
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to reject purchase order')
      );
    } finally {
      setIsRejecting(false);
    }
  };

  const handleClose = async () => {
    if (!purchaseOrder || !id) return;
    setIsClosing(true);
    try {
      await closePurchaseOrder(id, {
        updatedBy: currentUser(),
      });
      showSuccessToast('Success', 'Purchase order closed');
      fetchDetail();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to close purchase order')
      );
    } finally {
      setIsClosing(false);
    }
  };

  const handleReceive = async (items: ReceiveItemInput[]) => {
    if (!purchaseOrder || !id) return;
    setIsReceiving(true);
    setReceiveErrors(undefined);
    try {
      await receivePurchaseItems(id, {
        items,
        createdBy: currentUser(),
      });
      showSuccessToast('Success', 'Units received successfully');
      setIsReceiveOpen(false);
      // Status may transition APPROVED → RECEIVING, and the received list grows.
      fetchDetail();
      if (receivePage === 1) {
        fetchReceiveItems();
      } else {
        setReceivePage(1);
      }
    } catch (error) {
      // Nothing was written — the backend validates the whole batch first. Keep
      // the modal open with the rows intact and mark the ones it named, instead
      // of making ops rebuild a 200-row upload from a toast.
      if (error instanceof ReceivePurchaseItemsError) {
        setReceiveErrors(error.rows);
      }
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to receive units')
      );
    } finally {
      setIsReceiving(false);
    }
  };

  const renderActionButtons = () => {
    if (!purchaseOrder) return null;

    switch (purchaseOrder.status) {
      case PurchaseOrderStatus.CREATED:
        if (!isLayer1Approver) {
          return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Waiting for approval from Fleet Manager or Head of Operations.
            </div>
          );
        }
        return (
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              onClick={() => setIsRejectOpen(true)}
              disabled={isApproving}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleApprove}
              disabled={isApproving}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </div>
        );
      case PurchaseOrderStatus.PENDING_REVIEW:
        if (!isCooApprover) {
          return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Approved by Layer 1. Waiting for final sign-off from the COO.
            </div>
          );
        }
        return (
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              onClick={() => setIsRejectOpen(true)}
              disabled={isApproving}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleCooApprove}
              disabled={isApproving}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </div>
        );
      case PurchaseOrderStatus.APPROVED:
      case PurchaseOrderStatus.RECEIVING:
        return (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setIsReceiveOpen(true)}
            >
              <i className="ri-add-line text-[12px] mr-1" />
              Receive Units
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleClose}
              disabled={isClosing}
            >
              {isClosing ? 'Closing...' : 'Close'}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading purchase order details...
          </p>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Purchase Order Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The purchase order with ID "{id}" could not be found.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/purchase-orders')}
          >
            ← Back to Purchase Orders
          </Button>
        </div>
      </div>
    );
  }

  const receiveSubtitle = [
    purchaseOrder.code,
    [oemName, modelName].filter(Boolean).join(' '),
    `${purchaseOrder.totalUnits} unit total`,
    `${receivedTotal} already recorded`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5">
      <div className="mx-auto">
        <PurchaseOrderDetailHeader
          purchaseOrder={purchaseOrder}
          onBack={() => navigate('/purchase-orders')}
          actionButton={renderActionButtons()}
        />

        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <div className="w-full basis-[60%] flex-shrink-0 min-w-0">
            <PurchaseOrderDetailContent
              purchaseOrder={purchaseOrder}
              oemName={oemName}
              modelName={modelName}
              receiveItems={receiveItems}
              receivedTotal={receivedTotal}
              receivePage={receivePage}
              receivePageSize={receivePageSize}
              receiveLoading={receiveLoading}
              onReceivePageChange={setReceivePage}
              onReceivePageSizeChange={(size) => {
                setReceivePageSize(size);
                setReceivePage(1);
              }}
            />
          </div>

          <div className="w-full basis-[40%] flex-shrink-0 min-w-0">
            <PurchaseOrderStatusTimeline
              statusHistories={purchaseOrder.statusHistories || []}
            />
          </div>
        </div>
      </div>

      <RejectPurchaseOrderModal
        isOpen={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        onSubmit={handleReject}
        isSubmitting={isRejecting}
      />

      <ReceiveItemsModal
        isOpen={isReceiveOpen}
        onClose={() => {
          setIsReceiveOpen(false);
          setReceiveErrors(undefined);
        }}
        onSubmit={handleReceive}
        isSubmitting={isReceiving}
        subtitle={receiveSubtitle}
        purchaseOrderCode={purchaseOrder.code}
        requiresContractDuration={purchaseOrder.modelContractType === 'RTO'}
        submitErrors={receiveErrors}
      />
    </div>
  );
};

export default PurchaseOrderDetailPage;
